import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExternalLink, Play, FileText, Loader2, ArrowLeft, Database } from 'lucide-react';
import { promptService } from '../../../services/promptService';
import { schemaService } from '../../../services';
import {
  promptRefinementService,
  type RefinementSession,
  type CandidatePromptResponse,
  type CandidateSchemaResponse,
  type CandidateListItem,
  type FieldModification,
} from '../../../services/promptRefinementService';
import { type Prompt } from '../../../types';
import { NewRunModal } from '../NewRunModal';
import { FieldDefDiff } from './FieldDefDiff';

interface Props {
  session: RefinementSession | null;
  candidatePrompt: CandidatePromptResponse | null;
  candidateSchemaFromParent?: CandidateSchemaResponse | null;
}

/**
 * Detect whether accepted suggestions contain schema-targeted or prompt-targeted items.
 * BM1.7 suggestions have a `target` field ("schema" | "prompt") at the suggestion level.
 */
function detectAcceptedTargets(session: RefinementSession | null): {
  hasSchema: boolean;
  hasPrompt: boolean;
} {
  if (!session?.accepted_suggestions) {
    return { hasSchema: false, hasPrompt: false };
  }

  const accepted = session.accepted_suggestions as Record<string, unknown>;
  const fields = Array.isArray(accepted.fields) ? accepted.fields : [];

  let hasSchema = false;
  let hasPrompt = false;

  for (const fieldData of fields) {
    const suggestions = (fieldData as Record<string, unknown>).suggestions;
    if (!Array.isArray(suggestions)) continue;

    for (const s of suggestions) {
      const target = (s as Record<string, unknown>).target as string;
      if (target === 'schema') hasSchema = true;
      else if (target === 'prompt') hasPrompt = true;
    }
  }

  return { hasSchema, hasPrompt };
}

export function StepCandidat({ session, candidatePrompt, candidateSchemaFromParent }: Props) {
  const navigate = useNavigate();
  const [sourcePrompt, setSourcePrompt] = useState<Prompt | null>(null);
  const [candidateFullPrompt, setCandidateFullPrompt] = useState<Prompt | null>(null);
  const [candidateSchema, setCandidateSchema] = useState<CandidateSchemaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewRunModal, setShowNewRunModal] = useState(false);
  const [allSchemas, setAllSchemas] = useState<CandidateListItem[]>([]);
  const [allPrompts, setAllPrompts] = useState<CandidateListItem[]>([]);

  const { hasSchema, hasPrompt } = useMemo(
    () => detectAcceptedTargets(session),
    [session]
  );

  // Load all candidates for this session
  useEffect(() => {
    if (session?.id) {
      promptRefinementService.listSessionCandidates(session.id).then(data => {
        setAllSchemas(data.schemas);
        setAllPrompts(data.prompts);
      }).catch(() => {});
    }
  }, [session?.id, candidateSchema, candidatePrompt]);

  // Load prompt data (existing behavior)
  useEffect(() => {
    if (hasPrompt && (candidatePrompt?.id || session?.candidate_prompt_id)) {
      loadPrompts();
    }
  }, [candidatePrompt, session, hasPrompt]);

  // Use schema candidate from parent prop, or load from session
  useEffect(() => {
    if (candidateSchemaFromParent) {
      setCandidateSchema(candidateSchemaFromParent);
    } else if (hasSchema && session?.candidate_schema_id && !candidateSchema) {
      loadSchemaCandidateFromSession();
    }
  }, [candidateSchemaFromParent, session, hasSchema]);

  const loadSchemaCandidateFromSession = async () => {
    if (!session?.candidate_schema_id) return;
    try {
      setLoading(true);
      const schema = await schemaService.get(session.candidate_schema_id);

      // Build modification details from accepted_suggestions
      const modifications: FieldModification[] = [];
      const accepted = session.accepted_suggestions as Record<string, unknown> | null;
      if (accepted) {
        const fields = Array.isArray(accepted.fields) ? accepted.fields : [];
        for (const fieldData of fields) {
          const fd = fieldData as Record<string, unknown>;
          const fieldName = fd.field_name as string || '';
          const suggestions = Array.isArray(fd.suggestions) ? fd.suggestions : [];
          for (const s of suggestions) {
            const sugg = s as Record<string, unknown>;
            if (sugg.target === 'schema') {
              modifications.push({
                field_name: fieldName,
                sub_field: (sugg.sub_field as string) || '',
                current_value: (sugg.current_value as string) || null,
                proposed_value: (sugg.proposed_value as string) || '',
              });
            }
          }
        }
      }

      // Load parent schema for name/version
      const parentSchemaId = (schema as unknown as Record<string, unknown>).parent_schema_id as string | null;
      let parentName: string | null = null;
      let parentVersion: string | null = null;
      if (parentSchemaId) {
        try {
          const parent = await schemaService.get(parentSchemaId);
          parentName = parent.name;
          parentVersion = parent.version;
        } catch { /* ignore */ }
      }

      setCandidateSchema({
        id: schema.id,
        name: schema.name,
        version: schema.version,
        parent_schema_id: parentSchemaId,
        parent_schema_name: parentName,
        parent_schema_version: parentVersion,
        fields_modified: modifications,
        created_at: schema.created_at || '',
      });
    } catch (err) {
      console.error('Error loading schema candidate:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const candidateId = candidatePrompt?.id || session?.candidate_prompt_id;
      const sourceId = session?.source_prompt_id;

      if (candidateId) {
        const candidate = await promptService.get(candidateId);
        setCandidateFullPrompt(candidate);
      }
      if (sourceId) {
        const source = await promptService.get(sourceId);
        setSourcePrompt(source);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors du chargement des prompts');
    } finally {
      setLoading(false);
    }
  };


  // No accepted suggestions at all
  if (!session && !candidatePrompt) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800">Veuillez d'abord accepter des suggestions dans l'étape précédente.</p>
      </div>
    );
  }

  if (!hasSchema && !hasPrompt) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800 mb-3">Aucune suggestion acceptée — retourner à l'étape 2</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
        >
          <ArrowLeft size={14} />
          Retour aux suggestions
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  // Compute diff sections for prompt panel
  const sourceLines = (sourcePrompt?.content || '').split('\n');
  const candidateLines = (candidateFullPrompt?.content || '').split('\n');

  return (
    <div className="space-y-6">
      {/* ─── Schema Candidate Panel ─── */}
      {hasSchema && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Schéma candidat : {candidateSchema?.name || '—'} (v{candidateSchema?.version || '?'})
              </h2>
            </div>

            {candidateSchema ? (
              <>
                {/* Schema metadata */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <span className="text-sm text-gray-500">Version</span>
                    <p className="font-medium text-gray-900">{candidateSchema.version}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Schéma parent</span>
                    <p className="text-sm text-gray-700">
                      {candidateSchema.parent_schema_name
                        ? `${candidateSchema.parent_schema_name} (v${candidateSchema.parent_schema_version})`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Champs modifiés</span>
                    <p className="font-medium text-gray-900">{candidateSchema.fields_modified.length}</p>
                  </div>
                </div>

                {/* Field definition diffs */}
                <div className="space-y-3">
                  {candidateSchema.fields_modified.map((mod, i) => (
                    <FieldDefDiff
                      key={i}
                      fieldName={mod.field_name}
                      subField={mod.sub_field}
                      currentValue={mod.current_value}
                      proposedValue={mod.proposed_value}
                      applied
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm">Aucune donnée de schéma candidat disponible.</p>
            )}
          </div>

          {/* Schema actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {candidateSchema?.id && (
                <button
                  onClick={() => navigate(`/parametres/schemas/${candidateSchema.id}/edit`)}
                  className="flex items-center gap-2 text-primary hover:underline text-sm font-medium"
                >
                  <ExternalLink size={14} />
                  Ouvrir le schéma candidat
                </button>
              )}
              <button
                onClick={() => navigate('/parametres/schemas')}
                className="flex items-center gap-2 text-gray-500 hover:underline text-sm"
              >
                Voir tous les schémas
              </button>
            </div>

            <button
              onClick={() => setShowNewRunModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 font-medium"
            >
              <Play size={18} />
              Lancer benchmark avec ce schéma
            </button>
          </div>
        </div>
      )}

      {/* ─── Prompt Candidate Panel ─── */}
      {hasPrompt && (
        <div className="space-y-4">
          {/* Modifications summary */}
          {session?.accepted_suggestions && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Résumé des modifications (prompt)</h2>
              <div className="space-y-2">
                {Object.entries(session.accepted_suggestions as Record<string, Record<string, Record<string, unknown>>>).map(([fieldName, fieldDec]) => {
                  const acceptedItems = Object.entries(fieldDec).filter(
                    ([, val]) => val && (val as Record<string, unknown>).decision === 'accepted'
                  );
                  if (acceptedItems.length === 0) return null;
                  return (
                    <div key={fieldName} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                      <FileText size={16} className="text-gray-400" />
                      <span className="font-medium text-gray-900">{fieldName}</span>
                      <div className="flex gap-1">
                        {acceptedItems.map(([idx]) => {
                          const subField = getSuggestionSubField(session, fieldName, parseInt(idx, 10));
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                            >
                              {SUB_FIELD_LABELS[subField] || subField}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Diff visual */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Comparaison des prompts</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2 uppercase">Prompt source</h3>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 max-h-[500px] overflow-y-auto">
                  <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800">
                    {sourceLines.map((line, i) => {
                      const isModified = candidateLines[i] !== undefined && candidateLines[i] !== line;
                      return (
                        <div key={i} className={isModified ? 'bg-yellow-100 -mx-2 px-2' : ''}>
                          {line}
                        </div>
                      );
                    })}
                  </pre>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2 uppercase">Prompt candidat</h3>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 max-h-[500px] overflow-y-auto">
                  <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800">
                    {candidateLines.map((line, i) => {
                      const isModified = sourceLines[i] !== undefined && sourceLines[i] !== line;
                      const isNew = i >= sourceLines.length;
                      return (
                        <div key={i} className={isModified || isNew ? 'bg-yellow-100 -mx-2 px-2' : ''}>
                          {line}
                        </div>
                      );
                    })}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          {/* Candidate metadata */}
          {candidateFullPrompt && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Métadonnées du candidat</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Version</span>
                  <p className="font-medium text-gray-900">{candidateFullPrompt.version}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Nom</span>
                  <p className="font-medium text-gray-900">{candidateFullPrompt.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Statut</span>
                  <p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      candidateFullPrompt.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {candidateFullPrompt.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Parent prompt</span>
                  <p className="font-mono text-sm text-gray-700">
                    {(candidatePrompt?.parent_prompt_id || sourcePrompt?.id || '—').slice(0, 8)}…
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Prompt actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/parametres/prompts')}
              className="flex items-center gap-2 text-primary hover:underline text-sm font-medium"
            >
              <ExternalLink size={14} />
              Voir dans Paramètres → Prompts
            </button>

            {!hasSchema && (
              <button
                onClick={() => setShowNewRunModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 font-medium"
              >
                <Play size={18} />
                Lancer un benchmark avec ce prompt
              </button>
            )}
          </div>
        </div>
      )}

      {/* All candidates generated by this session */}
      {(allSchemas.length > 1 || allPrompts.length > 1) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Tous les candidats de cette session
          </h2>

          {allSchemas.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Schémas ({allSchemas.length})</h3>
              <div className="space-y-2">
                {allSchemas.map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded px-4 py-2">
                    <div>
                      <span className="text-sm font-medium">{s.name}</span>
                      <span className="text-xs text-gray-400 ml-2">v{s.version}</span>
                      {s.is_active && <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Actif</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString('fr-CA', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <button
                        onClick={() => navigate(`/parametres/schemas/${s.id}/edit`)}
                        className="text-primary hover:underline text-xs"
                      >
                        Ouvrir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {allPrompts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Prompts ({allPrompts.length})</h3>
              <div className="space-y-2">
                {allPrompts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded px-4 py-2">
                    <div>
                      <span className="text-sm font-medium">{p.name}</span>
                      <span className="text-xs text-gray-400 ml-2">v{p.version}</span>
                      {p.is_active && <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Actif</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-CA', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <button
                        onClick={() => navigate('/parametres/prompts')}
                        className="text-primary hover:underline text-xs"
                      >
                        Voir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Run Modal */}
      <NewRunModal
        isOpen={showNewRunModal}
        onClose={() => setShowNewRunModal(false)}
        onSuccess={() => {
          setShowNewRunModal(false);
          toast.success('Benchmark lancé');
          navigate('/benchmark/analyse');
        }}
        preSelectedSchemaId={candidateSchema?.id}
      />
    </div>
  );
}

const SUB_FIELD_LABELS: Record<string, string> = {
  notes: 'Notes / Warnings',
  format: 'Format attendu',
  description: 'Description',
  search_terms: 'Termes de recherche',
  examples_valid: 'Exemples valides',
  examples_invalid: 'Exemples invalides',
  allowed_values: 'Valeurs permises',
};

function getSuggestionSubField(session: RefinementSession, fieldName: string, idx: number): string {
  if (!session.suggestions) return 'notes';
  const raw = session.suggestions as Record<string, unknown>;

  const fieldEntries = Array.isArray(raw.fields)
    ? (raw.fields as Array<Record<string, unknown>>)
    : [];

  const fieldData = fieldEntries.find(f => f.field_name === fieldName);
  if (!fieldData) return 'notes';

  if (Array.isArray(fieldData.suggestions)) {
    const s = (fieldData.suggestions as Array<Record<string, unknown>>)[idx];
    if (s) {
      return (s.sub_field as string) || 'notes';
    }
  }

  return 'notes';
}
